import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useStaticQuery, withPrefix, graphql } from 'gatsby';
import Helmet from 'react-helmet';
import translationsSchema from './data/translationsSchema';

export default function Seo({
  pageTitle,
  pageDescription,
  pageKeywords,
  pathname,
  ogImage,
  country,
  city,
  coordinates,
  noindex,
}) {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            name
            siteUrl
            title
            description
            seo {
              separator
              og {
                fbAppId
                type
                title
                description
                image
              }
            }
          }
        }
      }
    `,
  );

  const [seoImage, setSeoImage] = useState(null);

  const countryTranslation = translationsSchema[process.env.GATSBY_COUNTRY_CODE];
  const { descriptionOrg, contactPoint, address, sameAs } = countryTranslation;

  const {
    siteUrl,
    title,
    description,
    seo: { og },
  } = site.siteMetadata || {};

  const schemaOrgWebPage = {
    '@context': 'http://schema.org',
    '@type': 'Organization',
    name: 'DR SMILE',
    description: descriptionOrg,
    legalName: 'Urban Technology GmbH',
    url: siteUrl,
    logo: 'https://drsmile.de/assets/img/logo_black.svg',
    foundingDate: '2016',
    founders: [
      { '@type': 'Person', name: 'Jens Urbaniak' },
      { '@type': 'Person', name: 'Chris von Wedemeyer' },
    ],
    address: {
      '@type': 'country',
      addressCountry: address.addressCountry,
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      telephone: contactPoint.telephone,
      email: contactPoint.email,
    },
    sameAs: sameAs,
  };

  // remove prefix from country code and format to uppercase
  const geoRegion = process.env.GATSBY_COUNTRY_CODE.replace(/.+-/, '').toUpperCase();

  // TODO: remove after NL and pt-PT is verified
  //  we need to verify facebook and ownership of the site to run Facebook campaigns
  const isNl = process.env.GATSBY_COUNTRY_CODE === 'nl';
  const isPt = process.env.GATSBY_COUNTRY_CODE === 'pt-PT';

  function metaImage(ogImage) {
    let metaImage;
    if (ogImage)
      if (ogImage?.file?.url) metaImage = 'https:' + ogImage.file.url;
      else metaImage = ogImage;
    else metaImage = siteUrl + withPrefix(og.image);

    return metaImage;
  }

  const keywords = pageKeywords ? pageKeywords.toString() : '';

  const [noIndexTag, setNoIndexTag] = useState(noindex);

  useEffect(() => {
    const host = window.location.host;
    const notAllowedDomains = ['gatsby.io', 'gtsb.io', '.sqa1.'];

    if (notAllowedDomains.some(domain => host.includes(domain))) setNoIndexTag(true);
  }, []);

  useEffect(() => {
    setSeoImage(metaImage(ogImage));
  }, []);

  return (
    <Helmet>
      {noIndexTag && <meta name="robots" content="noindex, nofollow" />}
      <title>{pageTitle || title}</title>
      <meta name="description" content={pageDescription || description} />
      <meta name="keywords" content={keywords} />
      <meta name="image" content={seoImage} />
      {country !== 'Österreich' && city && coordinates && <meta name="geo.region" content={geoRegion} />}
      {country !== 'Österreich' && city && coordinates && <meta name="geo.placename" content={city} />}
      {country !== 'Österreich' && city && coordinates && (
        <meta name="geo.position" content={`${coordinates.lat};${coordinates.lng}`} />
      )}
      {country !== 'Österreich' && city && coordinates && (
        <meta name="ICBM" content={`${coordinates.lat}, ${coordinates.lng}`} />
      )}

      {isNl && <meta name="facebook-domain-verification" content="pph6dgy3unhrxl8cs7ktme8l8wsdwo" />}
      {isPt && <meta name="facebook-domain-verification" content="ou5i8umvdsdx7lgekdq0t4p6x8joh5" />}

      {/* OpenGraph tags */}
      {og.fbAppId && <meta property="fb:app_id" content={og.fbAppId} />}
      <meta property="og:url" content={siteUrl + pathname} />
      <meta property="og:type" content={og.type} />
      <meta property="og:title" content={pageTitle || og.title} />
      <meta property="og:description" content={pageDescription || og.description || description} />
      <meta property="og:image" content={seoImage} />
      <script type="application/ld+json">{JSON.stringify(schemaOrgWebPage)}</script>
    </Helmet>
  );
}

Seo.defaultProps = {
  pathname: '/',
};

Seo.propTypes = {
  pageTitle: PropTypes.string,
  pageDescription: PropTypes.string,
  pathname: PropTypes.string,
  city: PropTypes.string,
  coordinates: PropTypes.shape({
    lat: PropTypes.string,
    lng: PropTypes.string,
  }),
};
